/*
 * File: AttendanceButtons.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';

import {
  getCoursePeriodQueryKey,
  getStudentScheduleQueryKey,
  takeAttendanceMutation,
} from '@drivingdoc/api-client/generated/react-query';
import type { AttendanceValue, CoursePeriod } from 'types/periods';

import useAuthContext from 'hooks/useAuthContext';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getAttendanceLabel } from 'utils/getAttendanceLabel';

import AttendanceStatus from 'components/AttendanceStatus';

// Configure dayjs with calendar plugin
dayjs.extend(calendar);

interface AttendanceButtonsProps {
  enrollment: CoursePeriod['period_enrollments'][0];
  onAttendanceChange?: (attendance: AttendanceValue) => void;
}

export default function AttendanceButtons({ enrollment, onAttendanceChange }: AttendanceButtonsProps) {
  const auth = useAuthContext();
  const queryClient = useQueryClient();
  const { attendance, attendance_taken_at, attendance_taken_by_user, canceled_at, course_period_id, student_user_id } =
    enrollment;

  const takeAttendance = useMutation({
    ...takeAttendanceMutation(),
    onSuccess: (_data, variables) => {
      const queryKey = getCoursePeriodQueryKey({ path: { id: course_period_id } });

      // Update the cache after successful mutation
      if (auth.user) {
        queryClient.setQueryData<CoursePeriod>(queryKey, (old) => {
          if (!old) return old;

          return {
            ...old,
            period_enrollments: old.period_enrollments.map((enrollment) => {
              if (enrollment.student_user_id === student_user_id) {
                return {
                  ...enrollment,
                  attendance: variables.body.attendanceRecords[0].attendance,
                  attendance_taken_at: dayjs().toISOString(),
                  attendance_taken_by_user: {
                    id: auth.user!.id,
                    title: auth.user!.title,
                    first_name: auth.user!.first_name,
                    last_name: auth.user!.last_name,
                    nickname: auth.user!.nickname,
                  },
                };
              }
              return enrollment;
            }),
          };
        });
      }
    },
    onSettled: () => {
      // Invalidate and refetch to ensure we have the correct server state
      queryClient.invalidateQueries({
        queryKey: getCoursePeriodQueryKey({ path: { id: course_period_id } }),
      });

      // Also invalidate student_course_periods to update drive history
      queryClient.invalidateQueries({
        queryKey: getStudentScheduleQueryKey({ path: { id: student_user_id } }),
        exact: false,
      });
    },
  });

  const handleAttendanceClick = (newAttendance: AttendanceValue) => {
    // Call the mutation
    takeAttendance.mutate({
      body: {
        coursePeriodId: course_period_id,
        attendanceRecords: [
          {
            studentId: student_user_id,
            attendance: newAttendance,
          },
        ],
      },
    });

    // Call the optional callback
    if (onAttendanceChange) {
      onAttendanceChange(newAttendance);
    }
  };

  // Disabled if enrollment is canceled or mutation is in progress
  const isDisabled = !!canceled_at || takeAttendance.isPending;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle2" fontWeight="bold">
          Attendance
        </Typography>
      </Box>

      {/* Error Alert */}
      {takeAttendance.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {getApiErrorMessage(takeAttendance.error) ?? 'Failed to update attendance. Please try again.'}
          </Typography>
        </Alert>
      )}

      {isDisabled && !takeAttendance.isPending ? (
        <Alert severity="info">Attendance marking disabled for canceled enrollment</Alert>
      ) : (
        <>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Button
                fullWidth
                variant={attendance === 'present' ? 'contained' : 'outlined'}
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleAttendanceClick('present')}
                disabled={isDisabled}
                sx={{ py: 2, minHeight: '56px' }}
              >
                {getAttendanceLabel('present')}
              </Button>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Button
                fullWidth
                variant={attendance === 'absent' ? 'contained' : 'outlined'}
                color="error"
                startIcon={<ErrorIcon />}
                onClick={() => handleAttendanceClick('absent')}
                disabled={isDisabled}
                sx={{ py: 2, minHeight: '56px' }}
              >
                {getAttendanceLabel('absent')}
              </Button>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Button
                fullWidth
                variant={attendance === 'no_permit' ? 'contained' : 'outlined'}
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => handleAttendanceClick('no_permit')}
                disabled={isDisabled}
                sx={{ py: 2, minHeight: '56px' }}
              >
                {getAttendanceLabel('no_permit')}
              </Button>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={() => handleAttendanceClick(null)}
                disabled={isDisabled || !attendance}
                sx={{ py: 2, minHeight: '56px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          <Collapse in={takeAttendance.isPending}>
            <Box py={1}>
              <LinearProgress />
            </Box>
          </Collapse>

          {/* Attendance Status */}
          <Collapse in={!takeAttendance.isPending}>
            <AttendanceStatus
              attendance={attendance}
              attendance_taken_at={attendance_taken_at}
              attendance_taken_by_user={attendance_taken_by_user}
            />
          </Collapse>

          {/* <Collapse in={!!(attendance !== null && !takeAttendance.isPending)}>
            <Alert
              severity={attendance === 'present' ? 'success' : 'warning'}
              icon={attendance === 'present' ? <CheckCircleIcon /> : <ErrorIcon />}
            >
              <Typography variant="body2">
                {`${attendance_taken_by_user?.nickname ?? attendance_taken_by_user?.first_name ?? ''} ${attendance_taken_by_user?.last_name ?? ''}`}
                marked this student as <strong>{getAttendanceLabel(attendance)}</strong>
                {` ${dayjs(attendance_taken_at).calendar(null, {
                  sameElse: 'MMMM D, YYYY [at] h:mm A',
                })}`}
              </Typography>
            </Alert>
          </Collapse> */}

          {/* <Collapse in={attendance === null && !takeAttendance.isPending}>
            <Alert severity="info">
              <Typography variant="body2">Attendance not yet marked</Typography>
            </Alert>
          </Collapse> */}
        </>
      )}
    </Box>
  );
}
